import { isEdge } from 'components/helpers/DomHelper'
import normalize from 'json-api-normalizer'
import emitter from 'common/util/events'
import { remove_non_ascii } from './functions'
import i18n from './internationalization'
import authenticationManager from 'businesslayer/authenticationManager'
import {
    getLocalStorageItem,
    getSessionStorageItem,
    removeSessionStorageItem,
    setSessionStorageItem,
    setLocalStorageItem
} from 'businesslayer/minutesLocalStore'
import { transientStorageManager } from 'businesslayer/minutesSessionStore'
import ssoConfig from 'config/ssoConfig.json'
import { v4 as uuid } from 'uuid'

const emptyHeaderName = 'unknown'

export const sanitizeResponse = (json) => {
    //Fixing some bad data case coming from server
    if (json?.included) {
        Object.keys(json.included).forEach((key) => {
            if (!json.included[key]) {
                delete json.included[key]
            }
        })
    }
}

export const addSortOrder = (normalized: NormalizedType, raw: RawType): NormalizedType => {
    if (!raw?.data || !Array.isArray(raw?.data)) {
        return normalized
    }

    const sortIds = raw.data.map((c) => c.id || -1).filter((c) => !isNaN(c) && c >= 0)

    if (sortIds.length) {
        normalized.sort = sortIds
    }

    return normalized
}

//Note: we use normalizer for json
export type RequestOptions = {
    shouldNormalize: boolean
}

export interface CustomError extends Error {}
const postProcessException = (exception) => {
    if (isEdge()) {
        const ua = window.navigator.userAgent
        const edge = ua.indexOf('Edge/')
        const edgeVersion = parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10)

        //That is how old Edge handles promise rejection...
        if (edgeVersion < 15 && exception.name === 'TypeMismatchError') {
            //
            emitter.emit('locked')
        }
    }
}
const parseResponse = async (response: Response): Promise<any> => {
    try {
        if (!response.ok) {
            throw new Error('Response not OK')
        }
        return await response.json()
    } catch (ex) {
        return processError(ex)
    }
}

const processError = (ex: Error): Promise<never> => {
    postProcessException(ex as CustomError)
    return Promise.reject(ex)
}

const makeNormalizeJSON = (shouldNormalize: boolean, shouldSanitize: boolean = true) => (
    json: JSONType
): JSONType => {
    if (shouldSanitize) {
        sanitizeResponse(json)
    }

    return shouldNormalize ? addSortOrder(normalize(json), json) : json
}
class FetchError extends Error {
    constructor(message?: string, public response?: any) {
        super(message) // 'Error' breaks prototype chain here
        Object.setPrototypeOf(this, new.target.prototype) // restore prototype chain
    }
}
const handleError = (response) => {
    const isNotification = getSessionStorageItem('isNotification')
    const sessionId = getLocalStorageItem('sessionId')

    if (response.status === 204) {
        return {}
    }
    if (response.status >= 200 && response.status < 300) {
        return response
    } else {
        const hasNotification = isNotification === 'true'

        if (response.status === 409) {
            response
                .json()
                .then((errorData) => {
                    if (errorData && errorData.source === 'DeepLink') {
                        setSessionStorageItem('isDeeplinkActionError', 'true')
                        setLocalStorageItem('isDeeplinkActionError', 'true')
                    }
                })
                .catch(() => {})
                .finally(() => {
                    const currentUrl = new URL(window.location.href)
                    const queryParams = currentUrl.search
                    window.location.href = `/not-found${queryParams}`
                })
            return
        }
        if (window.location.pathname === '/not-found') return
        if (response.status === 401 && !hasNotification) {
            const message = i18n.t('SESSION_EXPIRED_MESSAGE')
            removeSessionStorageItem('selectedStatusFilter')
            removeSessionStorageItem('selectedCommitteeFilter')
            const url = new URL(window.location.href)
            url.search = ''
            window.history.replaceState(null, '', url.toString())
            emitter.emit('locked', { message })
        }

        if (response.status === 422 && hasNotification) {
            removeSessionStorageItem('uuid')
            window.location.href = '/docError'
        }

        if (response.status === 412 && hasNotification) {
            removeSessionStorageItem('uuid')
            removeSessionStorageItem('isNotification')
            window.location.href = '/doc-not-exist'
        }

        if (response.status === 401 && hasNotification) {
            removeSessionStorageItem('uuid')
            removeSessionStorageItem('isNotification')
        }

        if (response.status === 410) {
            // These actions are common to both conditions
            removeSessionStorageItem('uuid')
            removeSessionStorageItem('isNotification')

            // When a user has an active session and tries to access a deep link URL that does not exist...
            if (sessionId) {
                window.location.href = '/url-not-exist'
            }
        }
        // When a user has an active session and tries to access a deep link URL that has expired...
        if (response.status === 423 && hasNotification && !!sessionId) {
            removeSessionStorageItem('uuid')
            window.location.href = '/unauthorize'
        }

        if (response.status === 403 && hasNotification) {
            setSessionStorageItem('forbidden', 'true')
        }

        let error
        try {
            error = new FetchError(response.statusText)
            error.response = response
        } catch (err) {
            console.error(err)
        }

        if (response.status === 422 && !hasNotification) {
            response.json().then((json) => {
                emitter.emit('error', json.error)
            })

            return Promise.reject(error)
        } else throw error
    }
}

const additionalHeaders = () => ({
    Authorization: authenticationManager.getApiToken(),
    SiteName: remove_non_ascii(authenticationManager.getSiteName()) || emptyHeaderName,
    CommitteeName: remove_non_ascii(authenticationManager.getCommitteeName()) || emptyHeaderName,
    'Accept-Language': i18n.language
})

const getStandardHeaders = () => ({
    accept: 'application/vnd.api+json',

    ...additionalHeaders()
})

const getRawPostHeaders = () => ({
    'content-type': 'application/json'
})

const getPostHeaders = () => ({
    'content-type': 'application/vnd.api+json',
    ...additionalHeaders()
})

const getOfficeHeaders = (isWord = true) => {
    const accept = isWord
        ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    return {
        accept,
        ...additionalHeaders()
    }
}

const getAdditionalPostHeader = () => ({
    'content-type': 'application/json',
    ...additionalHeaders()
})

// Headers for the template service with token
const TemplateDocuServiceBaseUrl =
    process.env.NODE_ENV === 'development'
        ? ssoConfig.template_doc_service_base_url
        : window._env_.TEMPLATE_DOCU_SERVICE_BASE_URL

const correlationId = uuid()

const additionalHeadersDocuService = () => ({
    Authorization: `Bearer ${
        transientStorageManager?.currentUser?.platform === 'boards'
            ? getLocalStorageItem('templateToken')
            : getSessionStorageItem('sessionId')
    }`,
    'x-correlation-id': correlationId,
    'correlation-id': correlationId,
    'Accept-Language': i18n.language
})

const additionalHeadersWithJsonDocuService = () => ({
    Authorization: `Bearer ${
        transientStorageManager?.currentUser?.platform === 'boards'
            ? getLocalStorageItem('templateToken')
            : getSessionStorageItem('sessionId')
    }`,
    'Content-Type': 'application/json',
    'x-correlation-id': correlationId,
    'correlation-id': correlationId,
    'Accept-Language': i18n.language
})

export const headers = {
    default: getStandardHeaders,
    raw: getRawPostHeaders,
    post: getPostHeaders,
    office: getOfficeHeaders
}
export {
    parseResponse,
    processError,
    makeNormalizeJSON,
    getStandardHeaders,
    getOfficeHeaders,
    getAdditionalPostHeader,
    getPostHeaders,
    getRawPostHeaders,
    handleError,
    TemplateDocuServiceBaseUrl,
    additionalHeadersDocuService,
    additionalHeadersWithJsonDocuService
}
export interface NormalizedType {
    success?: boolean
    sort?: number[]
}

export interface RawType {
    data?: { id?: number }[]
}
interface JSONType {}
